import clsx from 'clsx';
import { CheckCircle } from 'lucide-react';
import type { KidWithDetails } from '../../../stores/kidStore';
import { euros } from '../../../lib/orders/devis';
import {
  apresLaDateLimite,
  dateCourte,
  etatDeLaJournee,
  instantLisible,
  prixDuMoment,
  type ClasseDuCentre,
  type JourneeJpa,
} from '../../../lib/jpa/regles';

/*
  Étape 3 : les journées d'un enfant, une case par journée avec son prix. Une
  journée déjà réservée le dit ; une journée fermée à sa classe, ou pas encore
  ouverte, dit pourquoi au lieu d'offrir une case qu'un refus contredirait.
*/

interface Props {
  enfant: KidWithDetails;
  classe: ClasseDuCentre;
  journees: JourneeJpa[];
  /** session → enfants déjà inscrits */
  inscrits: Map<string, Set<string>>;
  estCochee: (sessionId: string) => boolean;
  tarifReduit: boolean;
  maintenant: Date;
  onBasculer: (sessionId: string) => void;
}

export default function JourneesDeLEnfant({
  enfant, classe, journees, inscrits, estCochee, tarifReduit, maintenant, onBasculer,
}: Props) {
  return (
    <section className="space-y-3 rounded-xl border border-gray-200 bg-white p-4">
      <p className="text-base font-extrabold">
        {enfant.prenom} <span className="text-sm font-semibold text-gray-500">· {classe.libelle}</span>
      </p>
      <div className="grid grid-cols-2 gap-2">
        {journees.map((j) => {
          const date = <span className="font-extrabold first-letter:uppercase">{dateCourte(j.date)}</span>;
          if (inscrits.get(j.sessionId)?.has(enfant.id)) {
            return (
              <p key={j.sessionId} className="flex min-h-[64px] flex-col justify-center gap-0.5 rounded-lg border border-green-200 bg-green-50 px-3 py-2 text-sm text-green-800">
                <span className="inline-flex items-center gap-1.5">
                  <CheckCircle className="h-4 w-4 flex-shrink-0" aria-hidden="true" />
                  {date}
                </span>
                <span className="font-semibold">Déjà inscrit·e</span>
              </p>
            );
          }
          const etat = etatDeLaJournee(j, maintenant);
          const raison = !j.classesAdmises.includes(classe.id)
            ? 'Pas ouverte à sa classe'
            : etat === 'a_venir' && j.ouverture
              ? `Inscriptions dès le ${instantLisible(j.ouverture)}`
              : etat !== 'ouverte'
                ? 'Inscriptions fermées'
                : null;
          if (raison) {
            return (
              <p key={j.sessionId} className="flex min-h-[64px] flex-col justify-center gap-0.5 rounded-lg border border-gray-200 bg-gray-50 px-3 py-2 text-sm text-gray-600">
                {date}
                <span>{raison}</span>
              </p>
            );
          }
          const cochee = estCochee(j.sessionId);
          const prix = prixDuMoment(j, tarifReduit, maintenant);
          return (
            <label
              key={j.sessionId}
              className={clsx(
                'flex min-h-[64px] cursor-pointer flex-col justify-center gap-0.5 rounded-lg px-3 py-2 text-sm',
                cochee ? 'border-2 border-primary-600 bg-primary-50' : 'border border-gray-300 bg-white hover:border-primary-300',
              )}
            >
              <span className="inline-flex items-center gap-2">
                <input
                  type="checkbox"
                  checked={cochee}
                  onChange={() => onBasculer(j.sessionId)}
                  className="h-5 w-5 flex-shrink-0 rounded border-gray-300 text-primary-600 focus:ring-primary-500"
                />
                {date}
              </span>
              <span className={clsx('pl-7 font-bold', cochee ? 'text-primary-800' : 'text-gray-800')}>
                {euros(prix.centimes)}
                {apresLaDateLimite(j, maintenant) && <span className="ml-1 font-normal text-gray-600">(après la date limite)</span>}
              </span>
            </label>
          );
        })}
      </div>
    </section>
  );
}
